import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '../environments/environment';
import { Observable, map, of, shareReplay, catchError } from 'rxjs';
import {
  FabricApiResponse,
  Metric,
  SupplierData,
  TableData,
} from './models/api.models';
import { TrendDirection } from './models/types';

export interface MetricSummary {
  total: number;
  trend: TrendDirection;
}

export interface UserPermissions {
  dashboards: Array<{
    userId: string;
    permission: string;
  }>;
  suppliers: Array<{
    userId: string;
    switchProvider: string;
  }>;
}

@Injectable({
  providedIn: 'root',
})
export class FabricDataService {
  private permissionsCache$: Observable<UserPermissions> | null = null;

  constructor(private httpClient: HttpClient) {}

  // Returns dashboard permissions from userPermissions.json (no Fabric dependency — always fast)
  getPermissions(): Observable<UserPermissions> {
    if (!this.permissionsCache$) {
      this.permissionsCache$ = this.httpClient
        .post<FabricApiResponse<UserPermissions>>(
          `${environment.msalConfig.nodeFabricHost}${environment.msalConfig.fetchFabricdataEndpoint}?graph_type=user-permissions`,
          {}
        )
        .pipe(
          map((response) => {
            if (response.status !== 200 || !response.result || !response.result.data) {
              return this.getCachedPermissions();
            }
            const permissions = response.result.data;
            // Cache to localStorage so we can fall back if the server is temporarily unavailable
            try {
              localStorage.setItem('perse_user_permissions', JSON.stringify(permissions));
            } catch (e) {}
            return permissions;
          }),
          catchError((error) => {
            console.error('Error fetching user permissions:', error);
            return of(this.getCachedPermissions());
          }),
          shareReplay(1),
        );
    }
    return this.permissionsCache$;
  }

  /** Returns last-known permissions from localStorage, or empty (no wrong defaults) */
  private getCachedPermissions(): UserPermissions {
    try {
      const cached = localStorage.getItem('perse_user_permissions');
      if (cached) {
        console.warn('insight-server unavailable — using cached permissions from localStorage');
        return JSON.parse(cached) as UserPermissions;
      }
    } catch (e) {}
    // No cache yet — return empty so no dashboard is shown rather than a wrong one
    return { dashboards: [], suppliers: [] };
  }

  // Fetches supplier list from Fabric via list_suppliers (independent of dashboard permissions)
  listSuppliers(): Observable<string[]> {
    return this.httpClient
      .post<FabricApiResponse<{ suppliers: Array<{ userId: string; switchProvider: string }> }>>(
        `${environment.msalConfig.nodeFabricHost}${environment.msalConfig.fetchFabricdataEndpoint}?graph_type=list_suppliers`,
        {}
      )
      .pipe(
        map((response) => {
          if (response.status !== 200 || !response.result?.data?.suppliers) {
            return [];
          }
          const suppliers = response.result.data.suppliers.map(
            (supplier) => supplier.switchProvider,
          );
          return Array.from(new Set(suppliers));
        }),
        catchError((error) => {
          console.error('Error fetching suppliers from Fabric:', error);
          return of([]); // Fail gracefully — supplier dropdown empty, other dashboards unaffected
        }),
      );
  }

  getDashboardPermissions(): Observable<string[]> {
    return this.getPermissions().pipe(
      map((permissions) => {
        const dashboards = permissions.dashboards.map(
          (dashboard) => dashboard.permission,
        );
        return Array.from(new Set(dashboards)); // Remove duplicates
      }),
    );
  }

  clearPermissionsCache(): void {
    this.permissionsCache$ = null;
  }

  //displaying the metric
  fetchFabricChartWithDate(
    graphType: string,
    fromDate: string,
    toDate: string,
    supplier: string = 'all',
  ): Observable<FabricApiResponse<Highcharts.Options>> {
    console.log(
      'Calling API with graphType:',
      graphType,
      fromDate,
      toDate,
      supplier,
    ); // Debugging line
    let url = `${environment.msalConfig.nodeFabricHost}${environment.msalConfig.fetchFabricdataEndpoint}?graph_type=${graphType}&from_date=${fromDate}&to_date=${toDate}`;

    // Add supplier parameter if not 'all'
    if (supplier && supplier !== 'all') {
      url += `&switch_provider=${encodeURIComponent(supplier)}`;
    }

    return this.httpClient.post<FabricApiResponse<Highcharts.Options>>(url, {});
  }

  //displaying the metric
  fetchFabricDataWithDate(
    graphType: string,
    fromDate: string,
    toDate: string,
    supplier: string = 'all',
  ): Observable<FabricApiResponse<Metric[]>> {
    console.log(
      'Calling API with graphType:',
      graphType,
      fromDate,
      toDate,
      supplier,
    ); // Debugging line
    let url = `${environment.msalConfig.nodeFabricHost}${environment.msalConfig.fetchFabricdataEndpoint}?graph_type=${graphType}&from_date=${fromDate}&to_date=${toDate}`;

    // Add supplier parameter if not 'all'
    if (supplier && supplier !== 'all') {
      url += `&switch_provider=${encodeURIComponent(supplier)}`;
    }

    return this.httpClient.post<FabricApiResponse<Metric[]>>(url, {});
  }

  //displaying the metric without date parameters
  fetchFabricData(
    graphType: string,
    supplier: string = 'all',
  ): Observable<FabricApiResponse<Metric[]>> {
    console.log('Calling API with graphType:', graphType, supplier); // Debugging line
    let url = `${environment.msalConfig.nodeFabricHost}${environment.msalConfig.fetchFabricdataEndpoint}?graph_type=${graphType}`;

    // Add supplier parameter if not 'all'
    if (supplier && supplier !== 'all') {
      url += `&switch_provider=${encodeURIComponent(supplier)}`;
    }

    return this.httpClient.post<FabricApiResponse<Metric[]>>(url, {});
  }

  //fetching table data
  fetchFabricTable(
    graphType: string,
    supplier: string = 'all',
  ): Observable<FabricApiResponse<TableData>> {
    console.log('Calling API for table with graphType:', graphType, supplier); // Debugging line
    let url = `${environment.msalConfig.nodeFabricHost}${environment.msalConfig.fetchFabricdataEndpoint}?graph_type=${graphType}`;

    // Add supplier parameter if not 'all'
    if (supplier && supplier !== 'all') {
      url += `&switch_provider=${encodeURIComponent(supplier)}`;
    }

    return this.httpClient.post<FabricApiResponse<TableData>>(url, {});
  }

  // fetching table data with date parameters
  fetchFabricTableWithDate(
    graphType: string,
    fromDate: string,
    toDate: string,
    supplier: string = 'all',
  ): Observable<FabricApiResponse<TableData>> {
    console.log(
      'Calling API for table with graphType:',
      graphType,
      fromDate,
      toDate,
      supplier,
    );
    let url = `${environment.msalConfig.nodeFabricHost}${environment.msalConfig.fetchFabricdataEndpoint}?graph_type=${graphType}&from_date=${fromDate}&to_date=${toDate}`;

    // Add supplier parameter if not 'all'
    if (supplier && supplier !== 'all') {
      url += `&switch_provider=${encodeURIComponent(supplier)}`;
    }

    return this.httpClient.post<FabricApiResponse<TableData>>(url, {});
  }
}
